import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Table, HeaderRow, TableCell, ColumnHeader, TableRow } from '../Shared/Tables';
import { PackageTrackingDetails } from '../../../types';
import HistoryRowComponent from './HistoryRowComponent';
import MaterialIcon from '../Shared/Icons/MaterialIcon';

const TableBody = styled.tbody`
    white-space: nowrap;
`;

const PhotoColumnHeader = styled(ColumnHeader)`
    width: 1px;
`;

const HistoryHeaderRow = styled(HeaderRow)`
    th {
        border-bottom: 1px solid white;
        border-right: 1px solid white;
        border-left: none;
    }
    th:last-child {
        border-right: none;
    }
`;

const ExpanderRow = styled(TableRow)`
    &:hover {
        & > * {
            transform: translateY(-1px);
        }
    }
`;

const Expander = styled.div`
    display: flex;
    align-items: center;
    padding-left: 10px;
`;

interface HistoryTableProps {
    packageDetails: PackageTrackingDetails;
    hideAllPhotos: boolean;
};

const HistoryTableComponent = ({ packageDetails, hideAllPhotos }: HistoryTableProps) => {
    const [t] = useTranslation();
    const showPhotos = !hideAllPhotos && packageDetails.UserOwnsPackage && packageDetails.HasPhotos;

    const [isExpanded, setIsExpanded] = useState(false);

    const histories = packageDetails.Histories.sort((d1, d2) => { return d1 > d2 ? 1 : d1 < d2 ? -1 : 0 });

    const columnHeaders = new Array<JSX.Element>();
    columnHeaders.push(<ColumnHeader key={'header_status'}>{t('mypackage_status')}</ColumnHeader>);
    columnHeaders.push(<ColumnHeader key={'header_time'}>{t('history_time')}</ColumnHeader>);
    if(packageDetails.ConsignmentMode)
        columnHeaders.push(<ColumnHeader key={'header_items'}>{t('history_items')}</ColumnHeader>);
    
    if(showPhotos)
        columnHeaders.push(<PhotoColumnHeader key={'header_photos'} />);
    
    return (
        <div className={'table-responsive'}>
            <Table id={'action-list'} className={'table table-sm table-striped rows'}>
                <thead>
                    <HistoryHeaderRow>
                        {columnHeaders}
                    </HistoryHeaderRow>
                </thead>
                
                <TableBody>
                    {histories.length > 3 ?
                    <>
                        {histories.map((history, index, histories) =>
                            <Fragment key={history.Id}>
                                <HistoryRowComponent
                                    history={history}
                                    isVisible={isExpanded || index === 0 || index === histories.length - 1}
                                    isConsignmentMode={packageDetails.ConsignmentMode}
                                    showPhotos={showPhotos}
                                />

                                {/* Put table expander after the first element */}
                                {!isExpanded && index === 0 &&
                                <>
                                    <ExpanderRow className={'interactive'} onClick={() => setIsExpanded(true)}>
                                        <TableCell colSpan={columnHeaders.length}>
                                            <Expander>
                                                <MaterialIcon>unfold_more</MaterialIcon>
                                                <span>{t('table_expand')}</span>
                                            </Expander>
                                        </TableCell>
                                    </ExpanderRow>

                                    {/* Hidden row to shade first and last row the same when table is contracted */}
                                    {histories.length % 2 === 1 &&
                                        <tr style={{visibility: 'collapse'}} />}
                                </>}
                            </Fragment>
                        )}

                        {/* Put contractor after the table */}
                        {isExpanded && 
                            <ExpanderRow className={'interactive'} onClick={() => setIsExpanded(false)}>
                                <TableCell colSpan={columnHeaders.length}>
                                    <Expander>
                                        <MaterialIcon>expand_less</MaterialIcon>
                                        <span>{t('table_contract')}</span>
                                    </Expander>
                                </TableCell>
                            </ExpanderRow>
                        }
                    </> : // histories.length <= 3 , so no expand/contract needed
                    <>
                        {histories.map((history) => 
                            <HistoryRowComponent
                                history={history}
                                isVisible={true}
                                isConsignmentMode={packageDetails.ConsignmentMode}
                                showPhotos={showPhotos}
                                key={history.Id}
                            />
                        )}
                    </>}
                </TableBody>
            </Table>
        </div>
    )
};

export default HistoryTableComponent;